import React, { useState, useRef, useEffect } from 'react';
import { geeService } from '../../services/geeService';
import { databaseService } from '../../services/databaseService';

const SAMPLE_PARKS = [
  {
    name: 'Ornek Park (Ankara)',
    coords: [[32.810332,39.893907],[32.810436,39.894158],[32.810559,39.894470],[32.810672,39.894744],[32.810854,39.894781],[32.810854,39.894464],[32.810825,39.894143],[32.810790,39.893851],[32.810332,39.893907]]
  },
];

const NDVIAnalysis = () => {
  const [geeStatus, setGeeStatus] = useState('idle');
  const [geeError, setGeeError] = useState(null);
  const [projectId, setProjectId] = useState(process.env.REACT_APP_GEE_PROJECT || '');
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [coordsText, setCoordsText] = useState(JSON.stringify(SAMPLE_PARKS[0].coords, null, 2));
  const [parkName, setParkName] = useState(SAMPLE_PARKS[0].name);
  const [dateStart, setDateStart] = useState('2024-04-01');
  const [dateEnd, setDateEnd] = useState('2024-10-31');
  const [cloudMax, setCloudMax] = useState(40);
  const [ndviThreshold, setNdviThreshold] = useState(0.35);
  const [mapTiles, setMapTiles] = useState(null);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [activeView, setActiveView] = useState('setup');
  const [detectionResults, setDetectionResults] = useState(null);
  const [detectLoading, setDetectLoading] = useState(false);
  const [detectionTiles, setDetectionTiles] = useState(null);
  const [detectionHistory, setDetectionHistory] = useState([]);
  const [typoResults, setTypoResults] = useState(null);
  const [typoLoading, setTypoLoading] = useState(false);
  const [typoTiles, setTypoTiles] = useState(null);
  const [typoHistory, setTypoHistory] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareParkId, setCompareParkId] = useState('');
  const [compareParkSearch, setCompareParkSearch] = useState('');
  const [compareParkList, setCompareParkList] = useState([]);
  const [compareDbData, setCompareDbData] = useState(null);
  const [compareUpdateStatus, setCompareUpdateStatus] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const detectMapRef = useRef(null);
  const detectMapInstanceRef = useRef(null);
  const typoMapRef = useRef(null);
  const typoMapInstanceRef = useRef(null);

  const handleInitGEE = async () => {
    try {
      setGeeStatus('loading');
      setGeeError(null);
      await geeService.initGEEWithProject(projectId);
      setGeeStatus('ready');
    } catch (e) {
      setGeeError(e.message);
      setGeeStatus('error');
    }
  };

  const handleAnalyze = async () => {
    try {
      setAnalyzing(true);
      setGeeError(null);
      const coords = JSON.parse(coordsText);
      const result = await geeService.computeNDVI(coords, dateStart, dateEnd, cloudMax, ndviThreshold);
      setResults({ ...result, parkName, coords });
      setAnalysisHistory(prev => [{ ...result, parkName, coords, timestamp: new Date().toLocaleString('tr-TR') }, ...prev]);
      setActiveView('results');

      try {
        const tiles = await geeService.getNDVIMapTiles(coords, dateStart, dateEnd);
        setMapTiles(tiles);
      } catch (tileErr) {
        console.warn('Harita katmanlari yuklenemedi:', tileErr);
      }
    } catch (e) {
      setGeeError(e.message);
    } finally {
      setAnalyzing(false);
    }
  };

  useEffect(() => {
    if (!results || !mapRef.current || !window.maplibregl) return;
    if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    const coords = results.coords;
    const lngs = coords.map(c => c[0]);
    const lats = coords.map(c => c[1]);
    const cLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
    const cLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const map = new window.maplibregl.Map({
      container: mapRef.current,
      style: `https://api.maptiler.com/maps/satellite/style.json?key=${process.env.REACT_APP_MAPTILER_KEY || 'eb6L6NoZoZhWFyDlpOhz'}`,
      center: [cLng, cLat],
      zoom: 17,
    });
    map.on('load', () => {
      map.addSource('park-boundary', {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] } },
      });
      map.addLayer({ id: 'park-fill', type: 'fill', source: 'park-boundary', paint: { 'fill-color': '#22c55e', 'fill-opacity': 0.15 } });
      map.addLayer({ id: 'park-line', type: 'line', source: 'park-boundary', paint: { 'line-color': '#facc15', 'line-width': 3 } });
      if (mapTiles?.ndviTileUrl) {
        map.addSource('ndvi-tiles', { type: 'raster', tiles: [mapTiles.ndviTileUrl], tileSize: 256 });
        map.addLayer({ id: 'ndvi-layer', type: 'raster', source: 'ndvi-tiles', paint: { 'raster-opacity': 0.7 } }, 'park-fill');
      }
    });
    mapInstanceRef.current = map;
    return () => { if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
  }, [results, mapTiles]);

  const loadSamplePark = (park) => {
    setParkName(park.name);
    setCoordsText(JSON.stringify(park.coords, null, 2));
  };

  const handleGreenDetection = async () => {
    try {
      setDetectLoading(true);
      setGeeError(null);
      const coords = JSON.parse(coordsText);
      const result = await geeService.computeGreenDetection(coords, dateStart, dateEnd, cloudMax);
      setDetectionResults({ ...result, parkName, coords });
      setDetectionHistory(prev => [{ ...result, parkName, coords, timestamp: new Date().toLocaleString('tr-TR') }, ...prev]);
      setActiveView('detection');

      try {
        const tiles = await geeService.getDetectionMapTiles(coords, dateStart, dateEnd);
        setDetectionTiles(tiles);
      } catch (tileErr) {
        console.warn('Detection harita katmanlari yuklenemedi:', tileErr);
      }
    } catch (e) {
      setGeeError(e.message);
    } finally {
      setDetectLoading(false);
    }
  };

  useEffect(() => {
    if (!detectionResults || !detectMapRef.current || !window.maplibregl) return;
    if (detectMapInstanceRef.current) { detectMapInstanceRef.current.remove(); detectMapInstanceRef.current = null; }
    const coords = detectionResults.coords;
    const lngs = coords.map(c => c[0]);
    const lats = coords.map(c => c[1]);
    const cLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
    const cLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const map = new window.maplibregl.Map({
      container: detectMapRef.current,
      style: `https://api.maptiler.com/maps/satellite/style.json?key=${process.env.REACT_APP_MAPTILER_KEY || 'eb6L6NoZoZhWFyDlpOhz'}`,
      center: [cLng, cLat],
      zoom: 17,
    });
    map.on('load', () => {
      map.addSource('park-boundary', {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] } },
      });
      map.addLayer({ id: 'park-line', type: 'line', source: 'park-boundary', paint: { 'line-color': '#facc15', 'line-width': 3 } });
      if (detectionTiles?.classifiedTileUrl) {
        map.addSource('class-tiles', { type: 'raster', tiles: [detectionTiles.classifiedTileUrl], tileSize: 256 });
        map.addLayer({ id: 'class-layer', type: 'raster', source: 'class-tiles', paint: { 'raster-opacity': 0.75 } }, 'park-line');
      }
    });
    detectMapInstanceRef.current = map;
    return () => { if (detectMapInstanceRef.current) { detectMapInstanceRef.current.remove(); detectMapInstanceRef.current = null; } };
  }, [detectionResults, detectionTiles]);

  const handleTypology = async () => {
    try {
      setTypoLoading(true);
      setGeeError(null);
      const coords = JSON.parse(coordsText);
      const result = await geeService.computeTypology(coords, dateStart, dateEnd, cloudMax);
      setTypoResults({ ...result, parkName, coords });
      setTypoHistory(prev => [{ ...result, parkName, coords, timestamp: new Date().toLocaleString('tr-TR') }, ...prev]);
      setActiveView('typology');

      try {
        const tiles = await geeService.getTypologyMapTiles(coords, dateStart, dateEnd);
        setTypoTiles(tiles);
      } catch (tileErr) {
        console.warn('Tipoloji harita katmanlari yuklenemedi:', tileErr);
      }
    } catch (e) {
      setGeeError(e.message);
    } finally {
      setTypoLoading(false);
    }
  };

  useEffect(() => {
    if (!typoResults || !typoMapRef.current || !window.maplibregl) return;
    if (typoMapInstanceRef.current) { typoMapInstanceRef.current.remove(); typoMapInstanceRef.current = null; }
    const coords = typoResults.coords;
    const lngs = coords.map(c => c[0]);
    const lats = coords.map(c => c[1]);
    const cLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
    const cLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const map = new window.maplibregl.Map({
      container: typoMapRef.current,
      style: `https://api.maptiler.com/maps/satellite/style.json?key=${process.env.REACT_APP_MAPTILER_KEY || 'eb6L6NoZoZhWFyDlpOhz'}`,
      center: [cLng, cLat],
      zoom: 17,
    });
    map.on('load', () => {
      map.addSource('park-boundary', {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] } },
      });
      map.addLayer({ id: 'park-line', type: 'line', source: 'park-boundary', paint: { 'line-color': '#facc15', 'line-width': 3 } });
      if (typoTiles?.typologyTileUrl) {
        map.addSource('typo-tiles', { type: 'raster', tiles: [typoTiles.typologyTileUrl], tileSize: 256 });
        map.addLayer({ id: 'typo-layer', type: 'raster', source: 'typo-tiles', paint: { 'raster-opacity': 0.75 } }, 'park-line');
      }
    });
    typoMapInstanceRef.current = map;
    return () => { if (typoMapInstanceRef.current) { typoMapInstanceRef.current.remove(); typoMapInstanceRef.current = null; } };
  }, [typoResults, typoTiles]);

  const getNdviColor = (val) => {
    if (val >= 0.6) return '#166534';
    if (val >= 0.4) return '#22c55e';
    if (val >= 0.2) return '#facc15';
    return '#ef4444';
  };

  const getNdviLabel = (val) => {
    if (val >= 0.6) return 'Cok Saglikli';
    if (val >= 0.4) return 'Saglikli';
    if (val >= 0.2) return 'Orta';
    if (val >= 0) return 'Zayif';
    return 'Bitki Olu';
  };

  const computeHealthScore = (r) => {
    if (!r) return null;
    const ndvi = r.ndviMean || 0;
    const greenPct = parseFloat(r.greenRatio || r.greenPercentage || 0);
    const totalArea = r.totalAreaM2 || 0;
    const greenArea = r.greenAreaM2 || r.totalGreenM2 || 0;

    let ndviScore = Math.min(Math.max((ndvi / 0.7) * 100, 0), 100);
    let greenScore = Math.min(greenPct * 1.25, 100);
    let coverageScore = greenArea > 0 ? Math.min((greenArea / Math.max(totalArea, 1)) * 150, 100) : 0;
    let overallScore = Math.round(ndviScore * 0.5 + greenScore * 0.3 + coverageScore * 0.2);
    overallScore = Math.min(overallScore, 100);

    let grade, gradeColor, gradeLabel, gradeBg;
    if (overallScore >= 85) { grade = 'A+'; gradeColor = '#166534'; gradeLabel = 'Mukemmel'; gradeBg = '#dcfce7'; }
    else if (overallScore >= 70) { grade = 'A'; gradeColor = '#22c55e'; gradeLabel = 'Cok Iyi'; gradeBg = '#f0fdf4'; }
    else if (overallScore >= 55) { grade = 'B'; gradeColor = '#84cc16'; gradeLabel = 'Iyi'; gradeBg = '#fefce8'; }
    else if (overallScore >= 40) { grade = 'C'; gradeColor = '#f59e0b'; gradeLabel = 'Orta'; gradeBg = '#fffbeb'; }
    else if (overallScore >= 25) { grade = 'D'; gradeColor = '#f97316'; gradeLabel = 'Zayif'; gradeBg = '#fff7ed'; }
    else { grade = 'F'; gradeColor = '#ef4444'; gradeLabel = 'Kritik'; gradeBg = '#fef2f2'; }

    const risks = [];
    if (ndvi < 0.2) risks.push({ level: 'kritik', text: 'NDVI cok dusuk — bitki ortusu neredeyse yok veya olmus', icon: '!!' });
    else if (ndvi < 0.35) risks.push({ level: 'uyari', text: 'NDVI dusuk — bitki ortusu zayif, sulama/bakim gerekebilir', icon: '!' });
    if (greenPct < 30) risks.push({ level: 'kritik', text: `Yesil alan orani cok dusuk (%${greenPct})`, icon: '!!' });
    else if (greenPct < 50) risks.push({ level: 'uyari', text: `Yesil alan orani dusuk (%${greenPct})`, icon: '!' });
    if (ndvi > 0.4 && greenPct > 60) risks.push({ level: 'pozitif', text: 'Bitki ortusu saglikli ve yaygin', icon: '+' });
    if (ndvi > 0.6) risks.push({ level: 'pozitif', text: 'Cok yogun ve saglikli bitki ortusu', icon: '+' });

    const recommendations = [];
    if (ndvi < 0.2) {
      recommendations.push('Acil sulama programi baslatin');
      recommendations.push('Toprak analizi yaptirin');
      recommendations.push('Yeni cim ekimi planlayin');
    } else if (ndvi < 0.35) {
      recommendations.push('Sulama sikligini artirin');
      recommendations.push('Gubreleme programi uygulayin');
      recommendations.push('Hastalık kontrolu yapin');
    } else if (ndvi < 0.5) {
      recommendations.push('Mevcut bakim programini surdurün');
      recommendations.push('Mevsimsel gubreleme yapin');
    }
    if (greenPct < 40) recommendations.push('Yeni yesil alan eklemeyi degerlendir');
    if (greenPct > 70 && ndvi > 0.4) recommendations.push('Mevcut bakim programini surdurün — sonuclar basarili');

    const categories = [
      { name: 'Bitki Sagligi (NDVI)', score: Math.round(ndviScore), max: 100, color: getNdviColor(ndvi), desc: `Ort. NDVI: ${ndvi.toFixed(3)}` },
      { name: 'Yesil Alan Kaplama', score: Math.round(greenScore), max: 100, color: greenPct > 50 ? '#22c55e' : greenPct > 30 ? '#f59e0b' : '#ef4444', desc: `%${greenPct} yesil alan` },
      { name: 'Alan Verimliligi', score: Math.round(coverageScore), max: 100, color: coverageScore > 60 ? '#22c55e' : '#f59e0b', desc: `${greenArea.toLocaleString()} m2 / ${totalArea.toLocaleString()} m2` },
    ];

    return { overallScore, grade, gradeColor, gradeLabel, gradeBg, ndviScore, greenScore, coverageScore, risks, recommendations, categories };
  };

  const healthData = computeHealthScore(results || detectionResults);

  const handleSearchPark = async () => {
    if (!compareParkSearch.trim()) return;
    try {
      setCompareLoading(true);
      const q = `SELECT park_id, name_kmz, name_xls, latitude, longitude FROM water_billing.park WHERE name_kmz ILIKE '%${compareParkSearch.replace(/'/g, "''")}%' OR name_xls ILIKE '%${compareParkSearch.replace(/'/g, "''")}%' ORDER BY park_id LIMIT 20`;
      const r = await databaseService.executeQuery(q);
      setCompareParkList(r.rows || []);
    } catch (e) { console.error(e); }
    finally { setCompareLoading(false); }
  };

  const handleSelectComparePark = async (parkId) => {
    try {
      setCompareParkId(parkId);
      setCompareLoading(true);
      setCompareDbData(null);

      const [parkRes, areaRes, irrRes] = await Promise.all([
        databaseService.executeQuery(`SELECT * FROM water_billing.park WHERE park_id = ${parkId}`),
        databaseService.executeQuery(`SELECT * FROM water_billing.park_area WHERE park_id = ${parkId}`),
        databaseService.executeQuery(`SELECT * FROM water_billing.park_irrigation WHERE park_id = ${parkId}`),
      ]);

      setCompareDbData({
        park: parkRes.rows?.[0] || null,
        area: areaRes.rows?.[0] || null,
        irrigation: irrRes.rows?.[0] || null,
      });
    } catch (e) { console.error(e); }
    finally { setCompareLoading(false); }
  };

  const buildComparisonRows = () => {
    if (!compareDbData) return [];
    const dbArea = compareDbData.area || {};
    const geeNdvi = results || {};
    const geeDet = detectionResults || {};
    const geeTypo = typoResults || {};
    const health = healthData || {};

    const rows = [];
    const num = (v) => v !== null && v !== undefined ? parseFloat(v) : null;
    const fmt = (v) => v !== null && v !== undefined ? Number(v).toLocaleString() : '—';
    const diff = (dbVal, geeVal) => {
      if (dbVal === null || geeVal === null) return null;
      const d = geeVal - dbVal;
      const pct = dbVal !== 0 ? ((d / dbVal) * 100).toFixed(1) : (geeVal !== 0 ? '100.0' : '0');
      return { diff: d, pct };
    };

    rows.push({
      category: 'Alan',
      field: 'Toplam Alan (m2)',
      dbValue: num(dbArea.total_area_m2),
      dbDisplay: fmt(dbArea.total_area_m2),
      geeValue: geeNdvi.totalAreaM2 || geeDet.totalAreaM2 || geeTypo.totalAreaM2 || null,
      geeDisplay: fmt(geeNdvi.totalAreaM2 || geeDet.totalAreaM2 || geeTypo.totalAreaM2),
      dbField: 'total_area_m2',
    });

    rows.push({
      category: 'Alan',
      field: 'Cim Alani (m2)',
      dbValue: num(dbArea.grass_area_m2),
      dbDisplay: fmt(dbArea.grass_area_m2),
      geeValue: geeTypo.typologies?.find(t => t.id === 'grass')?.area || null,
      geeDisplay: fmt(geeTypo.typologies?.find(t => t.id === 'grass')?.area),
      dbField: 'grass_area_m2',
    });

    rows.push({
      category: 'Alan',
      field: 'Agaclik Alan (m2)',
      dbValue: num(dbArea.woodland_area_m2),
      dbDisplay: fmt(dbArea.woodland_area_m2),
      geeValue: geeTypo.typologies?.find(t => t.id === 'tree')?.area || null,
      geeDisplay: fmt(geeTypo.typologies?.find(t => t.id === 'tree')?.area),
      dbField: 'woodland_area_m2',
    });

    rows.push({
      category: 'Alan',
      field: 'Sert Zemin (m2)',
      dbValue: num(dbArea.hard_surface_area_m2),
      dbDisplay: fmt(dbArea.hard_surface_area_m2),
      geeValue: geeTypo.typologies?.find(t => t.id === 'hard')?.area || geeDet.classes?.find(c => c.name === 'Yapili Alan')?.area || null,
      geeDisplay: fmt(geeTypo.typologies?.find(t => t.id === 'hard')?.area || geeDet.classes?.find(c => c.name === 'Yapili Alan')?.area),
      dbField: 'hard_surface_area_m2',
    });

    rows.push({
      category: 'Alan',
      field: 'Havuz Alani (m2)',
      dbValue: num(dbArea.pool_area_m2),
      dbDisplay: fmt(dbArea.pool_area_m2),
      geeValue: geeTypo.typologies?.find(t => t.id === 'water')?.area || geeDet.classes?.find(c => c.name === 'Su Yuzeyi')?.area || null,
      geeDisplay: fmt(geeTypo.typologies?.find(t => t.id === 'water')?.area || geeDet.classes?.find(c => c.name === 'Su Yuzeyi')?.area),
      dbField: 'pool_area_m2',
    });

    rows.push({
      category: 'Yesil Alan',
      field: 'Toplam Yesil Alan (m2)',
      dbValue: (() => { const g = num(dbArea.grass_area_m2); const w = num(dbArea.woodland_area_m2); return (g || 0) + (w || 0) || null; })(),
      dbDisplay: (() => { const g = num(dbArea.grass_area_m2); const w = num(dbArea.woodland_area_m2); return (g || w) ? ((g||0)+(w||0)).toLocaleString() : '—'; })(),
      geeValue: geeNdvi.greenAreaM2 || geeDet.totalGreenM2 || geeTypo.totalGreenM2 || null,
      geeDisplay: fmt(geeNdvi.greenAreaM2 || geeDet.totalGreenM2 || geeTypo.totalGreenM2),
    });

    rows.push({
      category: 'Yesil Alan',
      field: 'Yesil Alan Orani (%)',
      dbValue: (() => {
        const tot = num(dbArea.total_area_m2);
        const g = (num(dbArea.grass_area_m2) || 0) + (num(dbArea.woodland_area_m2) || 0);
        return tot && g ? parseFloat(((g / tot) * 100).toFixed(1)) : null;
      })(),
      dbDisplay: (() => {
        const tot = num(dbArea.total_area_m2);
        const g = (num(dbArea.grass_area_m2) || 0) + (num(dbArea.woodland_area_m2) || 0);
        return tot && g ? ((g / tot) * 100).toFixed(1) + '%' : '—';
      })(),
      geeValue: parseFloat(geeNdvi.greenRatio || geeDet.greenPercentage || geeTypo.greenPercentage || 0) || null,
      geeDisplay: (geeNdvi.greenRatio || geeDet.greenPercentage || geeTypo.greenPercentage) ? (geeNdvi.greenRatio || geeDet.greenPercentage || geeTypo.greenPercentage) + '%' : '—',
    });

    rows.push({
      category: 'NDVI',
      field: 'Ortalama NDVI',
      dbValue: null,
      dbDisplay: '—',
      geeValue: geeNdvi.ndviMean || geeDet.indices?.ndviMean || null,
      geeDisplay: (geeNdvi.ndviMean || geeDet.indices?.ndviMean) ? (geeNdvi.ndviMean || geeDet.indices?.ndviMean).toFixed(3) : '—',
    });

    rows.push({
      category: 'Saglik',
      field: 'Saglik Skoru',
      dbValue: null,
      dbDisplay: '—',
      geeValue: health.overallScore || null,
      geeDisplay: health.overallScore ? `${health.overallScore}/100 (${health.grade})` : '—',
    });

    if (compareDbData.irrigation) {
      const irr = compareDbData.irrigation;
      rows.push({
        category: 'Sulama',
        field: 'Sulama Alani (m2)',
        dbValue: num(irr.irrigated_area_m2),
        dbDisplay: fmt(irr.irrigated_area_m2),
        geeValue: geeNdvi.greenAreaM2 || geeDet.totalGreenM2 || null,
        geeDisplay: fmt(geeNdvi.greenAreaM2 || geeDet.totalGreenM2),
      });
      rows.push({
        category: 'Sulama',
        field: 'Oto Sulama (%)',
        dbValue: num(irr.auto_irrigation_percent),
        dbDisplay: irr.auto_irrigation_percent ? irr.auto_irrigation_percent + '%' : '—',
        geeValue: null,
        geeDisplay: '—',
      });
    }

    return rows.map(r => ({
      ...r,
      comparison: diff(r.dbValue, r.geeValue),
    }));
  };

  const handleUpdateDbFromGee = async (field, value) => {
    if (!compareParkId || !field || value === null) return;
    try {
      setCompareUpdateStatus({ field, status: 'loading' });
      await databaseService.executeWriteQuery(
        `UPDATE water_billing.park_area SET "${field}" = ${value} WHERE park_id = ${compareParkId}`
      );
      setCompareUpdateStatus({ field, status: 'ok' });
      handleSelectComparePark(compareParkId);
      setTimeout(() => setCompareUpdateStatus(null), 3000);
    } catch (e) {
      setCompareUpdateStatus({ field, status: 'error', message: e.message });
    }
  };

  const S = {
    wrap: { display: 'flex', gap: 0, height: 'calc(100vh - 200px)', background: '#f8fafc', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' },
    side: { width: 360, background: '#fff', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflow: 'auto' },
    main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
    section: { padding: '16px 20px', borderBottom: '1px solid #f1f5f9' },
    label: { fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' },
    input: { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
    textarea: { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12, fontFamily: 'monospace', outline: 'none', resize: 'vertical', boxSizing: 'border-box' },
    btn: (c, disabled) => ({ padding: '10px 16px', borderRadius: 8, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, background: disabled ? '#cbd5e1' : (c || '#3b82f6'), color: '#fff', width: '100%', transition: 'all .15s', opacity: disabled ? 0.6 : 1 }),
    btnSm: (c) => ({ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 500, background: c || '#3b82f6', color: '#fff' }),
    stat: (bg) => ({ background: bg || '#f8fafc', borderRadius: 10, padding: '16px', textAlign: 'center', border: '1px solid #e2e8f0' }),
    statVal: (c) => ({ fontSize: 28, fontWeight: 700, color: c || '#1e293b', lineHeight: 1.1 }),
    statLbl: { fontSize: 11, color: '#64748b', marginTop: 4 },
    alert: (t) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 13, background: t === 'err' ? '#fef2f2' : t === 'ok' ? '#f0fdf4' : '#eff6ff', color: t === 'err' ? '#dc2626' : t === 'ok' ? '#16a34a' : '#2563eb', border: `1px solid ${t === 'err' ? '#fecaca' : t === 'ok' ? '#bbf7d0' : '#bfdbfe'}`, marginBottom: 12 }),
    chip: (a) => ({ padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: a ? '#3b82f6' : '#f1f5f9', color: a ? '#fff' : '#64748b', transition: 'all .15s' }),
  };

  return (
    <div style={S.wrap}>
      <div style={S.side}>
        <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b' }}>Uydu NDVI Analizi</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>Google Earth Engine ile yesil alan analizi</p>
        </div>

        <div style={S.section}>
          <label style={S.label}>GEE Baglanti Durumu</label>
          {geeStatus === 'ready' ? (
            <div style={S.alert('ok')}>GEE baglantisin hazir</div>
          ) : (
            <>
              <input style={{ ...S.input, marginBottom: 8 }} value={projectId} onChange={e => setProjectId(e.target.value)} placeholder="GEE Project ID (orn: ee-pronuve)" />
              <button style={S.btn(geeStatus === 'loading' ? '#94a3b8' : '#10b981', geeStatus === 'loading')} onClick={handleInitGEE} disabled={geeStatus === 'loading'}>
                {geeStatus === 'loading' ? 'Baglaniyor...' : 'GEE Baglantisi Kur'}
              </button>
              {geeStatus === 'error' && <div style={{ ...S.alert('err'), marginTop: 8 }}>{geeError}</div>}
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>Google Earth Engine projenize erisim icin Google hesabinizla oturum acmaniz gerekir.</p>
            </>
          )}
        </div>

        <div style={S.section}>
          <label style={S.label}>Ornek Parklar</label>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {SAMPLE_PARKS.map((p, i) => (
              <button key={i} style={S.btnSm('#6366f1')} onClick={() => loadSamplePark(p)}>{p.name}</button>
            ))}
          </div>
        </div>

        <div style={S.section}>
          <label style={S.label}>Park Adi</label>
          <input style={S.input} value={parkName} onChange={e => setParkName(e.target.value)} />
        </div>

        <div style={S.section}>
          <label style={S.label}>Park Koordinatlari (GeoJSON [lng,lat])</label>
          <textarea style={{ ...S.textarea, height: 120 }} value={coordsText} onChange={e => setCoordsText(e.target.value)} />
        </div>

        <div style={S.section}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <label style={S.label}>Baslangic</label>
              <input type="date" style={S.input} value={dateStart} onChange={e => setDateStart(e.target.value)} />
            </div>
            <div>
              <label style={S.label}>Bitis</label>
              <input type="date" style={S.input} value={dateEnd} onChange={e => setDateEnd(e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
            <div>
              <label style={S.label}>Bulut Maks %</label>
              <input type="number" style={S.input} value={cloudMax} onChange={e => setCloudMax(Number(e.target.value))} />
            </div>
            <div>
              <label style={S.label}>NDVI Esik</label>
              <input type="number" step="0.01" style={S.input} value={ndviThreshold} onChange={e => setNdviThreshold(Number(e.target.value))} />
            </div>
          </div>
        </div>

        <div style={{ padding: '12px 20px' }}>
          <button style={S.btn(analyzing ? '#94a3b8' : '#3b82f6', analyzing || geeStatus !== 'ready')} onClick={handleAnalyze} disabled={analyzing || geeStatus !== 'ready'}>
            {analyzing ? 'Analiz Ediliyor...' : 'NDVI Analizi Baslat'}
          </button>
          <button style={{ ...S.btn(detectLoading ? '#94a3b8' : '#059669', detectLoading || geeStatus !== 'ready'), marginTop: 8 }} onClick={handleGreenDetection} disabled={detectLoading || geeStatus !== 'ready'}>
            {detectLoading ? 'Tespit Ediliyor...' : 'Yesil Alan Detection'}
          </button>
          <button style={{ ...S.btn(typoLoading ? '#94a3b8' : '#7c3aed', typoLoading || geeStatus !== 'ready'), marginTop: 8 }} onClick={handleTypology} disabled={typoLoading || geeStatus !== 'ready'}>
            {typoLoading ? 'Analiz Ediliyor...' : 'Tipoloji Analizi (Cim/Cali/Agac)'}
          </button>
        </div>

        {analysisHistory.length > 0 && (
          <div style={S.section}>
            <label style={S.label}>NDVI Gecmisi</label>
            {analysisHistory.map((h, i) => (
              <div key={i} style={{ padding: '8px 10px', background: '#f8fafc', borderRadius: 6, marginBottom: 4, cursor: 'pointer', fontSize: 12 }} onClick={() => { setResults(h); setActiveView('results'); }}>
                <strong>{h.parkName}</strong>
                <span style={{ float: 'right', color: '#64748b' }}>{h.timestamp}</span>
                <div style={{ color: getNdviColor(h.ndviMean), fontWeight: 600, marginTop: 2 }}>NDVI: {h.ndviMean?.toFixed(3)} - {getNdviLabel(h.ndviMean)}</div>
              </div>
            ))}
          </div>
        )}

        {detectionHistory.length > 0 && (
          <div style={S.section}>
            <label style={S.label}>Detection Gecmisi</label>
            {detectionHistory.map((h, i) => (
              <div key={i} style={{ padding: '8px 10px', background: '#f0fdf4', borderRadius: 6, marginBottom: 4, cursor: 'pointer', fontSize: 12 }} onClick={() => { setDetectionResults(h); setActiveView('detection'); }}>
                <strong>{h.parkName}</strong>
                <span style={{ float: 'right', color: '#64748b' }}>{h.timestamp}</span>
                <div style={{ color: '#166534', fontWeight: 600, marginTop: 2 }}>Yesil: {h.greenPercentage}% | {h.totalGreenM2?.toLocaleString()} m2</div>
              </div>
            ))}
          </div>
        )}

        {typoHistory.length > 0 && (
          <div style={S.section}>
            <label style={S.label}>Tipoloji Gecmisi</label>
            {typoHistory.map((h, i) => (
              <div key={i} style={{ padding: '8px 10px', background: '#faf5ff', borderRadius: 6, marginBottom: 4, cursor: 'pointer', fontSize: 12 }} onClick={() => { setTypoResults(h); setActiveView('typology'); }}>
                <strong>{h.parkName}</strong>
                <span style={{ float: 'right', color: '#64748b' }}>{h.timestamp}</span>
                <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                  {h.typologies?.filter(t => ['grass','shrub','tree'].includes(t.id)).map((t, ti) => (
                    <span key={ti} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: t.color + '22', color: t.color, fontWeight: 600 }}>{t.icon} {t.percentage}%</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={S.main}>
        <div style={{ display: 'flex', gap: 4, padding: '12px 20px', background: '#fff', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
          {[['setup','Bilgilendirme'], ['health','Saglik Gostergesi'], ['compare','Karsilastirma'], ['results','NDVI Sonuclar'], ['detection','Yesil Alan Detection'], ['typology','Tipoloji'], ['map','NDVI Harita'], ['detectionMap','Detection Harita'], ['typologyMap','Tipoloji Harita']].map(([v, label]) => (
            <button key={v} style={S.chip(activeView === v)} onClick={() => setActiveView(v)}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
          {geeError && <div style={S.alert('err')}>{geeError}</div>}

          {activeView === 'setup' && (
            <div style={{ maxWidth: 700 }}>
              <h3 style={{ color: '#1e293b', marginBottom: 16 }}>Google Earth Engine NDVI Analizi</h3>
              <div style={{ background: '#eff6ff', borderRadius: 10, padding: 20, marginBottom: 20, border: '1px solid #bfdbfe' }}>
                <h4 style={{ color: '#1d4ed8', margin: '0 0 8px' }}>NDVI Nedir?</h4>
                <p style={{ fontSize: 13, color: '#334155', margin: 0, lineHeight: 1.6 }}>
                  NDVI (Normalized Difference Vegetation Index), uydu goruntuleri uzerinden bitki sagligini ve yogunlugunu olcen bir indekstir.
                  Sentinel-2 uydusunun NIR (B8) ve Red (B4) bantlari kullanilarak hesaplanir.
                  Deger -1 ile +1 arasindadir. 0.35 ustu genellikle saglikli yesil alan olarak kabul edilir.
                </p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
                {[
                  { range: '< 0', color: '#dc2626', label: 'Su / Bos', bg: '#fef2f2' },
                  { range: '0 - 0.2', color: '#f59e0b', label: 'Zayif Bitki', bg: '#fffbeb' },
                  { range: '0.2 - 0.4', color: '#22c55e', label: 'Orta Bitki', bg: '#f0fdf4' },
                  { range: '> 0.4', color: '#166534', label: 'Yogun Bitki', bg: '#dcfce7' },
                ].map((item, i) => (
                  <div key={i} style={{ background: item.bg, borderRadius: 8, padding: 12, textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: item.color }}>{item.range}</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{item.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0' }}>
                <h4 style={{ color: '#1e293b', margin: '0 0 12px' }}>Nasil Kullanilir?</h4>
                <ol style={{ fontSize: 13, color: '#475569', margin: 0, paddingLeft: 20, lineHeight: 1.8 }}>
                  <li>Sol panelden GEE baglantisini kurun (Google hesabi gerekir)</li>
                  <li>Park koordinatlarini girin veya ornek parktan secin</li>
                  <li>Tarih araligi ve parametreleri ayarlayin</li>
                  <li>"NDVI Analizi Baslat" butonuna basin</li>
                  <li>Sonuclari ve haritayi inceleyin</li>
                </ol>
              </div>
            </div>
          )}

          {activeView === 'health' && (
            <div style={{ maxWidth: 800 }}>
              <h3 style={{ color: '#1e293b', marginBottom: 4 }}>Park Saglik Gostergesi</h3>
              <p style={{ color: '#64748b', fontSize: 12, marginBottom: 20 }}>NDVI ve yesil alan verilerinden otomatik saglik degerlendirmesi</p>

              {!healthData ? (
                <div>
                  <div style={{ background: '#fffbeb', borderRadius: 10, padding: 24, border: '1px solid #fde68a', marginBottom: 20, textAlign: 'center' }}>
                    <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>{''}</span>
                    <h4 style={{ color: '#92400e', margin: '0 0 8px' }}>Saglik Analizi Icin Veri Gerekiyor</h4>
                    <p style={{ fontSize: 13, color: '#a16207', margin: 0 }}>
                      {geeStatus !== 'ready'
                        ? 'Once sol panelden GEE baglantisini kurun, ardindan bir NDVI veya Yesil Alan Detection analizi calistirin.'
                        : 'GEE baglantisi hazir. Sol panelden "NDVI Analizi Baslat" veya "Yesil Alan Detection" butonuna basarak analiz yapin.'}
                    </p>
                  </div>

                  <div style={{ background: '#fff', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0', marginBottom: 20 }}>
                    <h4 style={{ color: '#1e293b', margin: '0 0 12px' }}>Saglik Gostergesi Nasil Hesaplanir?</h4>
                    <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.7, margin: '0 0 16px' }}>
                      Park saglik skoru, 3 temel parametrenin agirlikli ortalamasidir:
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                      {[
                        { weight: '50%', title: 'Bitki Sagligi', desc: 'Ortalama NDVI degeri — bitkilerin ne kadar saglikli oldugunu gosterir', color: '#22c55e', icon: '' },
                        { weight: '30%', title: 'Yesil Kaplama', desc: 'Toplam alanin yuzde kaci yesil alan ile kapli', color: '#3b82f6', icon: '' },
                        { weight: '20%', title: 'Alan Verimliligi', desc: 'Yesil alanin toplam alana orani — park ne kadar verimli kullaniliyor', color: '#f59e0b', icon: '' },
                      ].map((p, i) => (
                        <div key={i} style={{ background: '#f8fafc', borderRadius: 8, padding: 16, textAlign: 'center', border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: 28 }}>{p.icon}</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: p.color, margin: '4px 0' }}>{p.weight}</div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{p.title}</div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{p.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ background: '#fff', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0' }}>
                    <h4 style={{ color: '#1e293b', margin: '0 0 12px' }}>Not Skalasi</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
                      {[
                        { grade: 'A+', range: '85-100', label: 'Mukemmel', color: '#166534', bg: '#dcfce7' },
                        { grade: 'A', range: '70-84', label: 'Cok Iyi', color: '#22c55e', bg: '#f0fdf4' },
                        { grade: 'B', range: '55-69', label: 'Iyi', color: '#84cc16', bg: '#fefce8' },
                        { grade: 'C', range: '40-54', label: 'Orta', color: '#f59e0b', bg: '#fffbeb' },
                        { grade: 'D', range: '25-39', label: 'Zayif', color: '#f97316', bg: '#fff7ed' },
                        { grade: 'F', range: '0-24', label: 'Kritik', color: '#ef4444', bg: '#fef2f2' },
                      ].map((g, i) => (
                        <div key={i} style={{ background: g.bg, borderRadius: 8, padding: 10, textAlign: 'center' }}>
                          <div style={{ fontSize: 22, fontWeight: 800, color: g.color }}>{g.grade}</div>
                          <div style={{ fontSize: 10, color: '#64748b' }}>{g.range}</div>
                          <div style={{ fontSize: 11, color: g.color, fontWeight: 600 }}>{g.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 20, marginBottom: 24 }}>
                    <div style={{ background: healthData.gradeBg, borderRadius: 16, padding: 24, textAlign: 'center', border: `3px solid ${healthData.gradeColor}` }}>
                      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8, fontWeight: 600 }}>GENEL SAGLIK NOTU</div>
                      <div style={{ fontSize: 64, fontWeight: 800, color: healthData.gradeColor, lineHeight: 1 }}>{healthData.grade}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: healthData.gradeColor, marginTop: 4 }}>{healthData.gradeLabel}</div>
                      <div style={{ marginTop: 12, position: 'relative', height: 12, background: '#e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${healthData.overallScore}%`, background: healthData.gradeColor, borderRadius: 6, transition: 'width 1s ease' }} />
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: healthData.gradeColor, marginTop: 8 }}>{healthData.overallScore}/100</div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {healthData.categories.map((cat, i) => (
                        <div key={i} style={{ background: '#fff', borderRadius: 10, padding: 16, border: '1px solid #e2e8f0' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{cat.name}</span>
                            <span style={{ fontSize: 16, fontWeight: 700, color: cat.color }}>{cat.score}/100</span>
                          </div>
                          <div style={{ height: 10, background: '#f1f5f9', borderRadius: 5, overflow: 'hidden', marginBottom: 4 }}>
                            <div style={{ height: '100%', width: `${cat.score}%`, background: cat.color, borderRadius: 5, transition: 'width 1s ease' }} />
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>{cat.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {healthData.risks.length > 0 && (
                    <div style={{ background: '#fff', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0', marginBottom: 20 }}>
                      <h4 style={{ margin: '0 0 12px', color: '#1e293b' }}>Risk ve Durum Degerlendirmesi</h4>
                      <div style={{ display: 'grid', gap: 8 }}>
                        {healthData.risks.map((risk, i) => (
                          <div key={i} style={{
                            padding: '10px 14px', borderRadius: 8, fontSize: 13,
                            background: risk.level === 'kritik' ? '#fef2f2' : risk.level === 'uyari' ? '#fffbeb' : '#f0fdf4',
                            color: risk.level === 'kritik' ? '#dc2626' : risk.level === 'uyari' ? '#92400e' : '#166534',
                            border: `1px solid ${risk.level === 'kritik' ? '#fecaca' : risk.level === 'uyari' ? '#fde68a' : '#bbf7d0'}`,
                            display: 'flex', alignItems: 'center', gap: 8,
                          }}>
                            <span style={{ fontSize: 18 }}>{risk.icon}</span>
                            <span>{risk.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {healthData.recommendations.length > 0 && (
                    <div style={{ background: '#fff', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0', marginBottom: 20 }}>
                      <h4 style={{ margin: '0 0 12px', color: '#1e293b' }}>Oneriler</h4>
                      <div style={{ display: 'grid', gap: 8 }}>
                        {healthData.recommendations.map((rec, i) => (
                          <div key={i} style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#3b82f6', minWidth: 24 }}>{i + 1}.</span>
                            <span>{rec}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ background: '#fff', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0' }}>
                    <h4 style={{ margin: '0 0 12px', color: '#1e293b' }}>Saglik Gostergesi Ozeti</h4>
                    <div style={{ display: 'flex', height: 40, borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
                      <div style={{ width: `${healthData.overallScore}%`, background: `linear-gradient(90deg, ${healthData.gradeColor}cc, ${healthData.gradeColor})`, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'width 1s ease' }}>
                        <span style={{ fontSize: 13, color: '#fff', fontWeight: 700 }}>{healthData.overallScore}%</span>
                      </div>
                      <div style={{ flex: 1, background: '#f1f5f9' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8' }}>
                      <span>0 — Kritik</span>
                      <span>25 — Zayif</span>
                      <span>50 — Orta</span>
                      <span>75 — Iyi</span>
                      <span>100 — Mukemmel</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeView === 'compare' && (
            <div style={{ maxWidth: 900 }}>
              <h3 style={{ color: '#1e293b', marginBottom: 4 }}>GEE vs Veritabani Karsilastirmasi</h3>
              <p style={{ color: '#64748b', fontSize: 12, marginBottom: 20 }}>Uydu analizinden elde edilen degerler ile veritabanindaki mevcut degerleri karsilastirin</p>

              <div style={{ background: '#fff', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0', marginBottom: 20 }}>
                <h4 style={{ margin: '0 0 12px', color: '#1e293b' }}>Park Sec</h4>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <input
                    style={{ ...S.input, flex: 1, maxWidth: 350 }}
                    placeholder="Park adi ile ara... (Enter)"
                    value={compareParkSearch}
                    onChange={e => setCompareParkSearch(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSearchPark(); }}
                  />
                  <button style={S.btn(compareLoading ? '#94a3b8' : '#3b82f6', compareLoading)} onClick={handleSearchPark} disabled={compareLoading}>
                    {compareLoading ? 'Araniyor...' : 'Ara'}
                  </button>
                </div>
                {compareParkList.length > 0 && (
                  <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                    {compareParkList.map((p, i) => (
                      <div key={i} style={{
                        padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9',
                        background: compareParkId === p.park_id ? '#eff6ff' : 'transparent',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }} onClick={() => handleSelectComparePark(p.park_id)}>
                        <div>
                          <span style={{ fontWeight: 600, color: '#1e293b' }}>{p.name_kmz || p.name_xls}</span>
                          {p.name_xls && p.name_kmz !== p.name_xls && <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 6 }}>({p.name_xls})</span>}
                        </div>
                        <span style={{ fontSize: 11, color: '#64748b' }}>ID: {p.park_id}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {compareDbData && (
                <div>
                  <div style={{ background: '#eff6ff', borderRadius: 10, padding: 16, border: '1px solid #bfdbfe', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#1d4ed8' }}>{compareDbData.park?.name_kmz || 'Park'}</span>
                      <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>ID: {compareParkId}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {results && <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, background: '#dcfce7', color: '#166534', fontWeight: 600 }}>NDVI Mevcut</span>}
                      {detectionResults && <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, background: '#dcfce7', color: '#166534', fontWeight: 600 }}>Detection Mevcut</span>}
                      {typoResults && <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, background: '#dcfce7', color: '#166534', fontWeight: 600 }}>Tipoloji Mevcut</span>}
                      {!results && !detectionResults && !typoResults && <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, background: '#fef2f2', color: '#dc2626', fontWeight: 600 }}>GEE Analizi Yapilmadi</span>}
                    </div>
                  </div>

                  <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 20 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0', width: 60 }}>Kategori</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Alan</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#f59e0b', borderBottom: '2px solid #e2e8f0' }}>DB Degeri</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#3b82f6', borderBottom: '2px solid #e2e8f0' }}>GEE Degeri</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0' }}>Fark</th>
                          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0', width: 80 }}>Islem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {buildComparisonRows().map((row, i) => {
                          const hasDiff = row.comparison && Math.abs(row.comparison.diff) > 0.5;
                          const bigDiff = row.comparison && Math.abs(parseFloat(row.comparison.pct)) > 20;
                          return (
                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: bigDiff ? '#fefce8' : 'transparent' }}>
                              <td style={{ padding: '10px 16px', color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>{row.category}</td>
                              <td style={{ padding: '10px 16px', color: '#1e293b', fontWeight: 500 }}>{row.field}</td>
                              <td style={{ padding: '10px 16px', textAlign: 'right', color: row.dbValue !== null ? '#92400e' : '#cbd5e1', fontWeight: 600 }}>{row.dbDisplay}</td>
                              <td style={{ padding: '10px 16px', textAlign: 'right', color: row.geeValue !== null ? '#1d4ed8' : '#cbd5e1', fontWeight: 600 }}>{row.geeDisplay}</td>
                              <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                                {row.comparison ? (
                                  <span style={{
                                    fontSize: 12, fontWeight: 600,
                                    color: row.comparison.diff > 0 ? '#16a34a' : row.comparison.diff < 0 ? '#dc2626' : '#64748b',
                                  }}>
                                    {row.comparison.diff > 0 ? '+' : ''}{Math.round(row.comparison.diff).toLocaleString()}
                                    <span style={{ fontSize: 10, marginLeft: 4, color: '#94a3b8' }}>({row.comparison.pct}%)</span>
                                  </span>
                                ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                              </td>
                              <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                                {row.dbField && row.geeValue !== null && (
                                  <button
                                    style={{
                                      padding: '3px 8px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                                      background: compareUpdateStatus?.field === row.dbField && compareUpdateStatus?.status === 'ok' ? '#dcfce7'
                                        : compareUpdateStatus?.field === row.dbField && compareUpdateStatus?.status === 'loading' ? '#e2e8f0' : '#eff6ff',
                                      color: compareUpdateStatus?.field === row.dbField && compareUpdateStatus?.status === 'ok' ? '#166534' : '#2563eb',
                                    }}
                                    onClick={() => handleUpdateDbFromGee(row.dbField, row.geeValue)}
                                    disabled={compareUpdateStatus?.field === row.dbField && compareUpdateStatus?.status === 'loading'}
                                  >
                                    {compareUpdateStatus?.field === row.dbField && compareUpdateStatus?.status === 'ok' ? 'Guncellendi'
                                      : compareUpdateStatus?.field === row.dbField && compareUpdateStatus?.status === 'loading' ? '...' : 'DB Guncelle'}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {compareUpdateStatus?.status === 'error' && (
                    <div style={{ ...S.alert('err'), marginBottom: 16 }}>{compareUpdateStatus.message}</div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div style={{ background: '#fff', borderRadius: 10, padding: 16, border: '1px solid #e2e8f0' }}>
                      <h5 style={{ margin: '0 0 10px', color: '#92400e', fontSize: 13 }}>Veritabani (Mevcut)</h5>
                      {compareDbData.area ? (
                        <div style={{ display: 'grid', gap: 6 }}>
                          {Object.entries(compareDbData.area).filter(([k]) => k !== 'id' && k !== 'park_id').map(([k, v]) => (
                            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: '1px solid #f8fafc' }}>
                              <span style={{ color: '#64748b' }}>{k.replace(/_/g, ' ')}</span>
                              <span style={{ fontWeight: 600, color: v ? '#1e293b' : '#cbd5e1' }}>{v ? Number(v).toLocaleString() : 'null'}</span>
                            </div>
                          ))}
                        </div>
                      ) : <p style={{ fontSize: 12, color: '#94a3b8' }}>Bu park icin alan verisi bulunamadi.</p>}
                    </div>

                    <div style={{ background: '#fff', borderRadius: 10, padding: 16, border: '1px solid #e2e8f0' }}>
                      <h5 style={{ margin: '0 0 10px', color: '#1d4ed8', fontSize: 13 }}>GEE Analiz Sonuclari</h5>
                      {(results || detectionResults || typoResults) ? (
                        <div style={{ display: 'grid', gap: 6 }}>
                          {results && <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: '1px solid #f8fafc' }}>
                              <span style={{ color: '#64748b' }}>NDVI Ortalama</span>
                              <span style={{ fontWeight: 600, color: '#166534' }}>{results.ndviMean?.toFixed(3)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: '1px solid #f8fafc' }}>
                              <span style={{ color: '#64748b' }}>Yesil Alan</span>
                              <span style={{ fontWeight: 600 }}>{results.greenAreaM2?.toLocaleString()} m2 ({results.greenRatio}%)</span>
                            </div>
                          </>}
                          {typoResults && typoResults.typologies?.map((t, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: '1px solid #f8fafc' }}>
                              <span style={{ color: '#64748b' }}>{t.icon} {t.name}</span>
                              <span style={{ fontWeight: 600, color: t.color }}>{t.area?.toLocaleString()} m2 ({t.percentage}%)</span>
                            </div>
                          ))}
                          {healthData && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '6px 0', borderTop: '2px solid #e2e8f0', marginTop: 4 }}>
                              <span style={{ fontWeight: 600, color: '#1e293b' }}>Saglik Skoru</span>
                              <span style={{ fontWeight: 700, color: healthData.gradeColor }}>{healthData.overallScore}/100 ({healthData.grade})</span>
                            </div>
                          )}
                        </div>
                      ) : <p style={{ fontSize: 12, color: '#94a3b8' }}>Henuz GEE analizi yapilmadi. Sol panelden analiz calistirin.</p>}
                    </div>
                  </div>
                </div>
              )}

              {!compareDbData && !compareLoading && compareParkList.length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                  <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>{''}</span>
                  <p>Karsilastirma icin yukaridan bir park secin.</p>
                  <p style={{ fontSize: 12 }}>Park arayip sectiginizde, DB degerleri ile GEE analiz sonuclari yan yana goruntulenir.</p>
                </div>
              )}
            </div>
          )}

          {activeView === 'results' && results && (
            <div>
              <h3 style={{ color: '#1e293b', marginBottom: 4 }}>{results.parkName}</h3>
              <p style={{ color: '#64748b', fontSize: 12, marginBottom: 20 }}>{results.dateRange} | {results.imageCount} uydu goruntusu | Esik: {results.threshold}</p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
                <div style={S.stat('#f0fdf4')}>
                  <div style={S.statVal(getNdviColor(results.ndviMean))}>{results.ndviMean?.toFixed(3)}</div>
                  <div style={S.statLbl}>Ort. NDVI</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: getNdviColor(results.ndviMean), marginTop: 4 }}>{getNdviLabel(results.ndviMean)}</div>
                </div>
                <div style={S.stat()}>
                  <div style={S.statVal('#ef4444')}>{results.ndviMin?.toFixed(3)}</div>
                  <div style={S.statLbl}>Min NDVI</div>
                </div>
                <div style={S.stat()}>
                  <div style={S.statVal('#166534')}>{results.ndviMax?.toFixed(3)}</div>
                  <div style={S.statLbl}>Max NDVI</div>
                </div>
                <div style={S.stat('#eff6ff')}>
                  <div style={S.statVal('#2563eb')}>{results.greenAreaM2?.toLocaleString()}</div>
                  <div style={S.statLbl}>Yesil Alan (m2)</div>
                </div>
                <div style={S.stat()}>
                  <div style={S.statVal()}>{results.totalAreaM2?.toLocaleString()}</div>
                  <div style={S.statLbl}>Toplam Alan (m2)</div>
                </div>
                <div style={S.stat('#fefce8')}>
                  <div style={S.statVal('#ca8a04')}>{results.greenRatio}%</div>
                  <div style={S.statLbl}>Yesil Alan Orani</div>
                </div>
              </div>

              <div style={{ background: '#fff', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 12px', color: '#1e293b' }}>NDVI Dagilimi</h4>
                <div style={{ height: 30, borderRadius: 8, overflow: 'hidden', display: 'flex', position: 'relative' }}>
                  {(() => {
                    const range = results.ndviMax - results.ndviMin;
                    const meanPos = range > 0 ? ((results.ndviMean - results.ndviMin) / range) * 100 : 50;
                    return (
                      <>
                        <div style={{ flex: 1, background: 'linear-gradient(90deg, #ef4444, #facc15, #22c55e, #166534)' }} />
                        <div style={{ position: 'absolute', left: `${meanPos}%`, top: 0, bottom: 0, width: 3, background: '#1e293b', borderRadius: 2 }} />
                      </>
                    );
                  })()}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  <span>Min: {results.ndviMin?.toFixed(3)}</span>
                  <span>Ort: {results.ndviMean?.toFixed(3)}</span>
                  <span>Max: {results.ndviMax?.toFixed(3)}</span>
                </div>
              </div>
            </div>
          )}

          {activeView === 'results' && !results && (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
              <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>{''}</span>
              <p>Henuz analiz yapilmadi. Sol panelden analiz baslatabilirsiniz.</p>
            </div>
          )}

          {activeView === 'map' && (
            <div style={{ height: '100%', minHeight: 500 }}>
              {results ? (
                <div ref={mapRef} style={{ width: '100%', height: '100%', borderRadius: 10, overflow: 'hidden' }} />
              ) : (
                <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
                  <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>{''}</span>
                  <p>Haritayi gormek icin once NDVI analizi yapin.</p>
                </div>
              )}
            </div>
          )}

          {activeView === 'detection' && detectionResults && (
            <div>
              <h3 style={{ color: '#1e293b', marginBottom: 4 }}>{detectionResults.parkName} — Yesil Alan Detection</h3>
              <p style={{ color: '#64748b', fontSize: 12, marginBottom: 20 }}>{detectionResults.dateRange} | {detectionResults.imageCount} uydu goruntusu | Goruntu isleme ile arazi siniflandirma</p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
                <div style={S.stat('#f0fdf4')}>
                  <div style={S.statVal('#166534')}>{detectionResults.totalGreenM2?.toLocaleString()}</div>
                  <div style={S.statLbl}>Toplam Yesil Alan (m2)</div>
                </div>
                <div style={S.stat('#fefce8')}>
                  <div style={S.statVal('#ca8a04')}>{detectionResults.greenPercentage}%</div>
                  <div style={S.statLbl}>Yesil Alan Orani</div>
                </div>
                <div style={S.stat()}>
                  <div style={S.statVal()}>{detectionResults.totalAreaM2?.toLocaleString()}</div>
                  <div style={S.statLbl}>Toplam Alan (m2)</div>
                </div>
              </div>

              <div style={{ background: '#fff', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0', marginBottom: 20 }}>
                <h4 style={{ margin: '0 0 16px', color: '#1e293b' }}>Arazi Siniflandirma (Land Cover)</h4>
                <div style={{ display: 'grid', gap: 10 }}>
                  {detectionResults.classes.map((cls, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 16, height: 16, borderRadius: 4, background: cls.color, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{cls.name}</span>
                          <span style={{ fontSize: 12, color: '#64748b' }}>{cls.area?.toLocaleString()} m2 ({cls.percentage}%)</span>
                        </div>
                        <div style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.min(parseFloat(cls.percentage), 100)}%`, background: cls.color, borderRadius: 4, transition: 'width 0.5s ease' }} />
                        </div>
                        <span style={{ fontSize: 10, color: '#94a3b8' }}>{cls.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div style={{ background: '#fff', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 12px', color: '#1e293b' }}>NDVI Indeksi</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div style={S.stat('#f0fdf4')}><div style={{ fontSize: 18, fontWeight: 700, color: '#166534' }}>{detectionResults.indices.ndviMean?.toFixed(3)}</div><div style={S.statLbl}>Ortalama</div></div>
                    <div style={S.stat()}><div style={{ fontSize: 18, fontWeight: 700, color: '#64748b' }}>{detectionResults.indices.ndviStdDev?.toFixed(3)}</div><div style={S.statLbl}>Std Sapma</div></div>
                    <div style={S.stat()}><div style={{ fontSize: 18, fontWeight: 700, color: '#ef4444' }}>{detectionResults.indices.ndviMin?.toFixed(3)}</div><div style={S.statLbl}>Minimum</div></div>
                    <div style={S.stat()}><div style={{ fontSize: 18, fontWeight: 700, color: '#166534' }}>{detectionResults.indices.ndviMax?.toFixed(3)}</div><div style={S.statLbl}>Maksimum</div></div>
                  </div>
                </div>

                <div style={{ background: '#fff', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 12px', color: '#1e293b' }}>EVI Indeksi</h4>
                  <p style={{ fontSize: 11, color: '#94a3b8', margin: '0 0 8px' }}>Enhanced Vegetation Index — atmosferik duzeltmeli bitki indeksi</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    <div style={S.stat('#f0fdf4')}><div style={{ fontSize: 18, fontWeight: 700, color: '#166534' }}>{detectionResults.indices.eviMean?.toFixed(3)}</div><div style={S.statLbl}>Ortalama</div></div>
                    <div style={S.stat()}><div style={{ fontSize: 18, fontWeight: 700, color: '#ef4444' }}>{detectionResults.indices.eviMin?.toFixed(3)}</div><div style={S.statLbl}>Min</div></div>
                    <div style={S.stat()}><div style={{ fontSize: 18, fontWeight: 700, color: '#166534' }}>{detectionResults.indices.eviMax?.toFixed(3)}</div><div style={S.statLbl}>Max</div></div>
                  </div>
                </div>
              </div>

              <div style={{ background: '#fff', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 12px', color: '#1e293b' }}>Yesil Alan Dagilim Grafigi</h4>
                <div style={{ display: 'flex', height: 40, borderRadius: 8, overflow: 'hidden' }}>
                  {detectionResults.classes.filter(c => parseFloat(c.percentage) > 0).map((cls, i) => (
                    <div key={i} style={{ width: `${cls.percentage}%`, background: cls.color, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'width 0.5s ease', minWidth: parseFloat(cls.percentage) > 3 ? 'auto' : 0 }}>
                      {parseFloat(cls.percentage) > 8 && <span style={{ fontSize: 10, color: '#fff', fontWeight: 700 }}>{cls.percentage}%</span>}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 10 }}>
                  {detectionResults.classes.map((cls, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748b' }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: cls.color }} />
                      {cls.name}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeView === 'detection' && !detectionResults && (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
              <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>{''}</span>
              <p>Henuz yesil alan detection yapilmadi.</p>
              <p style={{ fontSize: 12 }}>Sol panelden "Yesil Alan Detection" butonuna basarak arazi siniflandirma analizi baslatin.</p>
            </div>
          )}

          {activeView === 'detectionMap' && (
            <div style={{ height: '100%', minHeight: 500 }}>
              {detectionResults ? (
                <>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 11, padding: '8px 12px', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                      {[
                        { color: '#94a3b8', label: 'Siniflandirilmamis' },
                        { color: '#d97706', label: 'Ciplak Toprak' },
                        { color: '#ef4444', label: 'Yapili Alan' },
                        { color: '#3b82f6', label: 'Su' },
                        { color: '#a3e635', label: 'Seyrek Bitki' },
                        { color: '#22c55e', label: 'Saglikli Bitki' },
                        { color: '#166534', label: 'Yogun Bitki' },
                      ].map((item, i) => (
                        <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ width: 10, height: 10, borderRadius: 2, background: item.color, display: 'inline-block' }} />
                          {item.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div ref={detectMapRef} style={{ width: '100%', height: 'calc(100% - 50px)', borderRadius: 10, overflow: 'hidden' }} />
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
                  <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>{''}</span>
                  <p>Detection haritasini gormek icin once Yesil Alan Detection yapin.</p>
                </div>
              )}
            </div>
          )}

          {activeView === 'typology' && typoResults && (
            <div>
              <h3 style={{ color: '#1e293b', marginBottom: 4 }}>{typoResults.parkName} — Bitki Tipolojisi</h3>
              <p style={{ color: '#64748b', fontSize: 12, marginBottom: 20 }}>{typoResults.dateRange} | {typoResults.imageCount} uydu goruntusu | Red Edge + SWIR bant analizi</p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
                <div style={S.stat('#f0fdf4')}>
                  <div style={S.statVal('#166534')}>{typoResults.totalGreenM2?.toLocaleString()}</div>
                  <div style={S.statLbl}>Toplam Yesil Alan (m2)</div>
                </div>
                <div style={S.stat('#fefce8')}>
                  <div style={S.statVal('#ca8a04')}>{typoResults.greenPercentage}%</div>
                  <div style={S.statLbl}>Yesil Oran</div>
                </div>
                <div style={S.stat('#eff6ff')}>
                  <div style={S.statVal('#2563eb')}>{typoResults.moisture?.toFixed(3)}</div>
                  <div style={S.statLbl}>Nem Indeksi</div>
                </div>
                <div style={S.stat()}>
                  <div style={S.statVal()}>{typoResults.totalAreaM2?.toLocaleString()}</div>
                  <div style={S.statLbl}>Toplam Alan (m2)</div>
                </div>
              </div>

              <div style={{ background: '#fff', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0', marginBottom: 20 }}>
                <h4 style={{ margin: '0 0 16px', color: '#1e293b' }}>Bitki Tipolojisi Siniflandirmasi</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
                  {typoResults.typologies.filter(t => ['grass','shrub','tree'].includes(t.id)).map((t, i) => (
                    <div key={i} style={{ background: '#f8fafc', borderRadius: 10, padding: 16, border: `2px solid ${t.color}`, textAlign: 'center' }}>
                      <div style={{ fontSize: 32, marginBottom: 6 }}>{t.icon}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 2 }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>{t.desc}</div>
                      <div style={{ fontSize: 24, fontWeight: 700, color: t.color }}>{t.area?.toLocaleString()} m2</div>
                      <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{t.percentage}% alan orani</div>
                      {t.avgNdvi > 0 && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Ort. NDVI: {t.avgNdvi?.toFixed(3)}</div>}
                    </div>
                  ))}
                </div>

                <div style={{ display: 'grid', gap: 10 }}>
                  {typoResults.typologies.map((t, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 20, width: 28, textAlign: 'center' }}>{t.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{t.name}</span>
                          <span style={{ fontSize: 12, color: '#64748b' }}>{t.area?.toLocaleString()} m2 ({t.percentage}%)</span>
                        </div>
                        <div style={{ height: 10, background: '#f1f5f9', borderRadius: 5, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.min(parseFloat(t.percentage), 100)}%`, background: t.color, borderRadius: 5, transition: 'width 0.5s ease' }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ background: '#fff', borderRadius: 10, padding: 20, border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 12px', color: '#1e293b' }}>Tipoloji Dagilim Grafigi</h4>
                <div style={{ display: 'flex', height: 48, borderRadius: 8, overflow: 'hidden' }}>
                  {typoResults.typologies.filter(t => parseFloat(t.percentage) > 0).map((t, i) => (
                    <div key={i} style={{ width: `${t.percentage}%`, background: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', transition: 'width 0.5s ease', minWidth: parseFloat(t.percentage) > 5 ? 'auto' : 0 }}>
                      {parseFloat(t.percentage) > 10 && <span style={{ fontSize: 14 }}>{t.icon}</span>}
                      {parseFloat(t.percentage) > 8 && <span style={{ fontSize: 9, color: '#fff', fontWeight: 700 }}>{t.percentage}%</span>}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 10 }}>
                  {typoResults.typologies.map((t, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748b' }}>
                      <span>{t.icon}</span>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: t.color }} />
                      {t.name} ({t.percentage}%)
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeView === 'typology' && !typoResults && (
            <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
              <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>{''}</span>
              <p>Henuz tipoloji analizi yapilmadi.</p>
              <p style={{ fontSize: 12 }}>Sol panelden "Tipoloji Analizi" butonuna basarak cim/cali/agac siniflandirmasi baslatin.</p>
            </div>
          )}

          {activeView === 'typologyMap' && (
            <div style={{ height: '100%', minHeight: 500 }}>
              {typoResults ? (
                <>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 11, padding: '8px 12px', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                      {[
                        { color: '#e2e8f0', label: 'Siniflandirilmamis' },
                        { color: '#86efac', label: 'Cim' },
                        { color: '#f59e0b', label: 'Cali' },
                        { color: '#166534', label: 'Agac' },
                        { color: '#a3e635', label: 'Karisik Bitki' },
                        { color: '#3b82f6', label: 'Su' },
                        { color: '#94a3b8', label: 'Sert Zemin' },
                        { color: '#d97706', label: 'Ciplak Toprak' },
                      ].map((item, i) => (
                        <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ width: 10, height: 10, borderRadius: 2, background: item.color, display: 'inline-block' }} />
                          {item.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div ref={typoMapRef} style={{ width: '100%', height: 'calc(100% - 50px)', borderRadius: 10, overflow: 'hidden' }} />
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
                  <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>{''}</span>
                  <p>Tipoloji haritasini gormek icin once Tipoloji Analizi yapin.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NDVIAnalysis;
